import { Fragment, useMemo, useState } from 'react';
import type { ChartDataset } from 'chart.js';
import { Download, RotateCcw, Search } from 'lucide-react';
import { ChartBox } from '../components/ChartBox';
import { Tag } from '../components/Tag';
import type { SalesPeriod, SecondhandSaleLine, SecondhandSalesResponse } from '../types';
import { num } from '../utils';

// Doanh số hàng cũ (đơn có SKU -QSD) — đọc /api/care/secondhand-sales, mọi tính toán ở client:
// lượng đơn nhỏ (vài chục/tháng) nên trả dòng chi tiết là đủ cho KPI, chart, pivot và bảng.

interface Props {
  data: SecondhandSalesResponse | null;
  busy: boolean;
  error: string;
  range: string;
  // Page-level target from the pinned bar ('' = all): LCD / PCD / LTD / PED.
  target?: string;
}

// f_sales cat_1_name → BOD target (the 4 level-1 categories of datamart_sales.f_sales).
const CAT1_TARGET: Record<string, string> = {
  'Màn hình Group': 'LCD', 'Thiết bị PC': 'PCD', 'Máy tính xách tay Laptop': 'LTD', Peripherals: 'PED',
};

type Gran = 'day' | 'week' | 'month';
const USED_COLOR = '#7c3aed';
const NEW_COLOR = '#0891b2';
const PREV_COLOR = '#94a3b8';
const PIE_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626', '#64748b', '#db2777'];

interface Totals {
  orders: number; bundledOrders: number; usedQty: number; usedRev: number; newRev: number; total: number;
}

function totalsOf(lines: SecondhandSaleLine[]): Totals {
  const orders = new Map<string, { used: number; fresh: number }>();
  let usedQty = 0; let usedRev = 0; let newRev = 0;
  for (const l of lines) {
    const o = orders.get(l.oid) || { used: 0, fresh: 0 };
    if (l.used) { usedQty += l.qty; usedRev += l.rev; o.used += l.rev; } else { newRev += l.rev; o.fresh += 1; }
    orders.set(l.oid, o);
  }
  const bundledOrders = [...orders.values()].filter(o => o.fresh > 0).length;
  return { orders: orders.size, bundledOrders, usedQty, usedRev, newRev, total: usedRev + newRev };
}

// Doanh thu bán kèm (hàng mới) của 1 đơn chia cho từng SKU cũ theo tỷ trọng doanh thu hàng cũ trong đơn.
interface PivotRow { sku: string; name: string; orders: number; qty: number; usedRev: number; bundled: number; total: number }
function pivotOf(lines: SecondhandSaleLine[]): Map<string, PivotRow> {
  const byOrder = new Map<string, SecondhandSaleLine[]>();
  for (const l of lines) byOrder.set(l.oid, [...(byOrder.get(l.oid) || []), l]);
  const rows = new Map<string, PivotRow & { oids: Set<string> }>();
  for (const [oid, ls] of byOrder) {
    const used = ls.filter(l => l.used);
    if (!used.length) continue;
    const newRev = ls.filter(l => !l.used).reduce((s, l) => s + l.rev, 0);
    const usedSum = used.reduce((s, l) => s + l.rev, 0);
    for (const l of used) {
      const share = usedSum > 0 ? l.rev / usedSum : 1 / used.length;
      const r = rows.get(l.sku) || { sku: l.sku, name: l.name || '', orders: 0, qty: 0, usedRev: 0, bundled: 0, total: 0, oids: new Set<string>() };
      r.oids.add(oid); r.orders = r.oids.size;
      r.qty += l.qty; r.usedRev += l.rev; r.bundled += newRev * share; r.total = r.usedRev + r.bundled;
      rows.set(l.sku, r);
    }
  }
  return rows as unknown as Map<string, PivotRow>;
}

const vnd = (n: number) => Math.round(n).toLocaleString('vi-VN');
const mil = (n: number) => {
  const v = n / 1_000_000;
  return `${v.toLocaleString('vi-VN', { maximumFractionDigits: Math.abs(v) >= 100 ? 0 : 1 })} tr`;
};
const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const periodText = (p: SalesPeriod) => (p.start === p.end ? ddmm(p.start) : `${ddmm(p.start)} → ${ddmm(p.end)}`);

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function isoWeek(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7) + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d.getTime() - firstThu.getTime()) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
const bucketOf = (iso: string, g: Gran) => (g === 'day' ? iso : g === 'month' ? iso.slice(0, 7) : isoWeek(iso));
const bucketLabel = (code: string) =>
  /^\d{4}-W\d{2}$/.test(code) ? `T${code.slice(6)}` : /^\d{4}-\d{2}$/.test(code) ? `Th${code.slice(5)}/${code.slice(2, 4)}` : ddmm(code);

// % thay đổi so kỳ trước. prev=null → không có kỳ so sánh (range "Tất cả").
interface Delta { text: string; tone: 'green' | 'red' | 'gray' }
function deltaOf(cur: number, prev: number | null, pp = false): Delta | null {
  if (prev == null) return null;
  if (pp) {
    const d = Math.round((cur - prev) * 10) / 10;
    return { text: `${d > 0 ? '+' : ''}${d.toLocaleString('vi-VN')} điểm`, tone: d > 0 ? 'green' : d < 0 ? 'red' : 'gray' };
  }
  if (prev === 0) return cur > 0 ? { text: 'mới', tone: 'green' } : { text: '0%', tone: 'gray' };
  const d = Math.round(((cur - prev) / prev) * 1000) / 10;
  return { text: `${d > 0 ? '▲ +' : d < 0 ? '▼ ' : ''}${d.toLocaleString('vi-VN')}%`, tone: d > 0 ? 'green' : d < 0 ? 'red' : 'gray' };
}

function SalesKpi({ label, value, prevText, delta, tone }: { label: string; value: string; prevText?: string; delta: Delta | null; tone: string }) {
  return (
    <div className="card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value tnum" style={{ color: tone }}>{value}</div>
      <div className="kpi-sub">
        {delta ? <><Tag tone={delta.tone}>{delta.text}</Tag> <span className="muted">kỳ trước {prevText}</span></> : <span className="muted">Không có kỳ so sánh</span>}
      </div>
    </div>
  );
}

export function SecondhandSalesSection({ data, busy, error, range, target = '' }: Props) {
  const [store, setStore] = useState('');
  const [sale, setSale] = useState('');
  const [query, setQuery] = useState('');
  const [granPick, setGranPick] = useState<Gran | ''>('');
  const [detailLimit, setDetailLimit] = useState(150);

  const meta = data?.meta;
  // Target keeps whole orders whose used item is in the target: bundled new lines stay with their order.
  const allLines = useMemo(() => {
    const lines = data?.lines || [];
    if (!target) return lines;
    const keep = new Set(lines.filter(l => l.used && CAT1_TARGET[l.cat || ''] === target).map(l => l.oid));
    return lines.filter(l => keep.has(l.oid));
  }, [data, target]);
  const prevPeriod = meta?.prev || null;

  const matches = (l: SecondhandSaleLine) => (!store || l.store === store) && (!sale || l.sp === sale);
  const q = query.trim().toLowerCase();
  // Tìm SKU/tên: giữ nguyên cả đơn có dòng khớp, để doanh thu bán kèm không bị cắt.
  const orderHit = useMemo(() => {
    if (!q) return null;
    return new Set(allLines.filter(l => `${l.sku} ${l.name || ''} ${l.code || ''}`.toLowerCase().includes(q)).map(l => l.oid));
  }, [allLines, q]);
  const scoped = allLines.filter(l => matches(l) && (!orderHit || orderHit.has(l.oid)));
  const inCur = (l: SecondhandSaleLine) => !!meta && l.d >= meta.cur.start && l.d <= meta.cur.end;
  const cur = scoped.filter(inCur);
  const prev = prevPeriod ? scoped.filter(l => l.d >= prevPeriod.start && l.d <= prevPeriod.end) : [];

  const storeOptions = useMemo(() => [...new Set(allLines.map(l => l.store))].sort(), [allLines]);
  const saleOptions = useMemo(() => [...new Set(allLines.filter(l => !store || l.store === store).map(l => l.sp))].sort((a, b) => a.localeCompare(b, 'vi')), [allLines, store]);

  const t = totalsOf(cur);
  const p = prevPeriod ? totalsOf(prev) : null;
  const bundleRate = t.orders ? (t.bundledOrders / t.orders) * 100 : 0;
  const prevBundleRate = p ? (p.orders ? (p.bundledOrders / p.orders) * 100 : 0) : null;
  const aov = t.orders ? t.total / t.orders : 0;
  const prevAov = p ? (p.orders ? p.total / p.orders : 0) : null;

  // ---- trend: bucket kỳ đang xem; kỳ trước dời +N ngày để chồng đúng vị trí ----
  const days = meta?.cur.days || null;
  const firstCur = cur.length ? cur.reduce((m, l) => (l.d < m ? l.d : m), cur[0].d) : meta?.cur.end || '';
  const trendStart = meta ? (days ? meta.cur.start : firstCur) : '';
  const spanDays = meta && trendStart ? Math.round((Date.parse(meta.cur.end) - Date.parse(trendStart)) / 86400000) + 1 : 0;
  const gran: Gran = granPick || (spanDays <= 31 ? 'day' : spanDays <= 120 ? 'week' : 'month');
  const buckets: string[] = [];
  if (meta && trendStart) {
    for (let d = trendStart; d <= meta.cur.end; d = addDays(d, 1)) {
      const b = bucketOf(d, gran);
      if (buckets[buckets.length - 1] !== b) buckets.push(b);
    }
  }
  const sumBy = (ls: SecondhandSaleLine[], pick: (l: SecondhandSaleLine) => boolean, shift = 0) => {
    const m = new Map<string, number>();
    for (const l of ls) if (pick(l)) { const b = bucketOf(shift ? addDays(l.d, shift) : l.d, gran); m.set(b, (m.get(b) || 0) + l.rev); }
    return m;
  };
  const usedBy = sumBy(cur, l => l.used);
  const newBy = sumBy(cur, l => !l.used);
  const prevBy = days ? sumBy(prev, () => true, days) : new Map<string, number>();
  const ordersBy = new Map<string, Set<string>>();
  for (const l of cur) { const b = bucketOf(l.d, gran); ordersBy.set(b, (ordersBy.get(b) || new Set()).add(l.oid)); }
  const toMil = (v: number | undefined) => Math.round(((v || 0) / 1_000_000) * 10) / 10;
  // Stacked bars: hide per-segment labels, print the stack total once on the top segment.
  const NO_LABEL = { display: false };
  const totalLabel = (totals: number[]) => ({
    display: (ctx: { dataIndex: number }) => (totals[ctx.dataIndex] || 0) > 0,
    formatter: (_v: unknown, ctx: { dataIndex: number }) => totals[ctx.dataIndex].toLocaleString('vi-VN'),
  });
  const trendTotals = buckets.map(b => toMil((usedBy.get(b) || 0) + (newBy.get(b) || 0)));
  const maxOrders = Math.max(1, ...buckets.map(b => ordersBy.get(b)?.size || 0));

  // ---- tỷ trọng ----
  const shareBy = (key: (l: SecondhandSaleLine) => string, ls: SecondhandSaleLine[]) => {
    const m = new Map<string, number>();
    for (const l of ls) m.set(key(l), (m.get(key(l)) || 0) + l.rev);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  };
  const storeShare = shareBy(l => l.store, cur);
  const saleShare = shareBy(l => l.sp, cur).slice(0, 10);
  const saleUsed = new Map(shareBy(l => l.sp, cur.filter(l => l.used)));

  // ---- pivot ----
  const pivotCur = pivotOf(cur);
  const pivotPrev = pivotOf(prev);
  const pivotRows = [...pivotCur.values()].sort((a, b) => b.total - a.total);
  const prevOnly = [...pivotPrev.values()].filter(r => !pivotCur.has(r.sku)).sort((a, b) => b.total - a.total);

  // ---- chi tiết (theo đơn, mới nhất trước; dòng hàng cũ đứng đầu đơn) ----
  const detail = cur;
  const orderIndex = new Map<string, number>();
  detail.forEach(l => { if (!orderIndex.has(l.oid)) orderIndex.set(l.oid, orderIndex.size); });

  const exportCsv = () => {
    const head = ['Ngày', 'Mã đơn', 'Sale', 'Cửa hàng', 'SKU', 'Sản phẩm', 'Cũ/Mới', 'SL', 'Đơn giá', 'Chiết khấu', 'Thành tiền', 'Trạng thái'];
    const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = detail.map(l => [l.d, l.code || l.oid, l.sp, l.store, l.sku, l.name, l.used ? 'Hàng cũ' : 'Hàng mới', l.qty,
      Math.round(l.list / Math.max(1, l.qty)), l.disc, l.rev, l.st].map(cell).join(','));
    const blob = new Blob([`﻿${[head.join(','), ...rows].join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `doanh-so-hang-cu-${range}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const filtered = !!(store || sale || q);
  const head = (
    <div className="section-title">
      <h2>Doanh số hàng cũ (mã QSD)</h2>
      <div className="ins-actions">
        <select className={`ins-select ${store ? 'has-value' : ''}`} aria-label="Cửa hàng" value={store} onChange={e => { setStore(e.target.value); setSale(''); }}>
          <option value="">Mọi cửa hàng</option>
          {storeOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={`ins-select ${sale ? 'has-value' : ''}`} aria-label="Sale" value={sale} onChange={e => setSale(e.target.value)}>
          <option value="">Mọi sale</option>
          {saleOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className={`ins-search ${query ? 'has-value' : ''}`} title="Tìm SKU, tên sản phẩm, mã đơn">
          <Search size={14} />
          <input type="search" value={query} placeholder="SKU, sản phẩm, mã đơn…" onChange={e => setQuery(e.target.value)} />
        </label>
        <button type="button" className="ins-clear" onClick={() => { setStore(''); setSale(''); setQuery(''); }} disabled={!filtered} title="Xóa lọc doanh số"><RotateCcw size={15} /><span>Xóa lọc</span></button>
        <button type="button" className="ins-iconbtn" onClick={exportCsv} disabled={!detail.length} title="Xuất CSV chi tiết đơn" aria-label="Xuất CSV"><Download size={15} /></button>
      </div>
    </div>
  );

  if (error && !data) {
    return <section className="card space">{head}<div className="muted">Không tải được doanh số hàng cũ: {error}</div></section>;
  }
  if (!meta) {
    return <section className="card space">{head}<div className="muted">{busy ? 'Đang tải doanh số hàng cũ từ f_sales…' : 'Chưa có dữ liệu doanh số.'}</div></section>;
  }

  const cmpText = prevPeriod ? `so với kỳ trước ${periodText(prevPeriod)}` : 'Tất cả thời gian · không có kỳ so sánh';

  return (
    <>
      <section id="secondhand-sales" className="card space ins-anchor">
        {head}
        <div className="muted" style={{ marginTop: -4 }}>
          Kỳ đang xem <b>{periodText(days ? meta.cur : { ...meta.cur, start: firstCur || meta.cur.start })}</b> · {cmpText}
          {' · '}đơn có ít nhất 1 SKU hàng cũ (-QSD), đã bỏ dòng hủy · doanh thu sau chiết khấu (gồm VAT){busy ? ' · đang làm mới…' : ''}
        </div>
      </section>

      <section className="grid kpis">
        <SalesKpi label="Đơn có hàng cũ" value={num(t.orders)} prevText={p ? num(p.orders) : ''} delta={deltaOf(t.orders, p?.orders ?? null)} tone="var(--blue)" />
        <SalesKpi label="SL hàng cũ" value={num(t.usedQty)} prevText={p ? num(p.usedQty) : ''} delta={deltaOf(t.usedQty, p?.usedQty ?? null)} tone="var(--violet)" />
        <SalesKpi label="DT hàng cũ" value={mil(t.usedRev)} prevText={p ? mil(p.usedRev) : ''} delta={deltaOf(t.usedRev, p?.usedRev ?? null)} tone="var(--violet)" />
        <SalesKpi label="DT bán kèm (hàng mới)" value={mil(t.newRev)} prevText={p ? mil(p.newRev) : ''} delta={deltaOf(t.newRev, p?.newRev ?? null)} tone="var(--cyan)" />
        <SalesKpi label="Tổng DT đơn hàng cũ" value={mil(t.total)} prevText={p ? mil(p.total) : ''} delta={deltaOf(t.total, p?.total ?? null)} tone="var(--green)" />
        <SalesKpi label="% đơn có bán kèm" value={`${bundleRate.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%`} prevText={prevBundleRate != null ? `${prevBundleRate.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%` : ''} delta={deltaOf(bundleRate, prevBundleRate, true)} tone="var(--teal)" />
        <SalesKpi label="Giá trị TB / đơn" value={mil(aov)} prevText={prevAov != null ? mil(prevAov) : ''} delta={deltaOf(aov, prevAov)} tone="var(--ink)" />
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Xu hướng doanh thu đơn hàng cũ</h2>
          <div className="ins-seg small">
            {(['day', 'week', 'month'] as Gran[]).map(g => (
              <button key={g} type="button" aria-pressed={gran === g} onClick={() => setGranPick(g)}>{g === 'day' ? 'Ngày' : g === 'week' ? 'Tuần' : 'Tháng'}</button>
            ))}
          </div>
        </div>
        <div className="chart">
          <ChartBox
            labels={buckets.map(bucketLabel)}
            datasets={[
              { label: 'DT hàng cũ (tr)', data: buckets.map(b => toMil(usedBy.get(b))), type: 'bar', stack: 'rev', backgroundColor: `${USED_COLOR}bb`, borderRadius: 4, maxBarThickness: 40, datalabels: NO_LABEL } as unknown as ChartDataset,
              { label: 'DT bán kèm mới (tr)', data: buckets.map(b => toMil(newBy.get(b))), type: 'bar', stack: 'rev', backgroundColor: `${NEW_COLOR}99`, borderRadius: 4, maxBarThickness: 40, datalabels: totalLabel(trendTotals) } as unknown as ChartDataset,
              ...(days ? [{ label: 'Tổng DT kỳ trước (tr)', data: buckets.map(b => toMil(prevBy.get(b))), type: 'line', borderColor: PREV_COLOR, borderDash: [5, 4], backgroundColor: 'transparent', borderWidth: 2, pointRadius: 2, tension: 0.3, datalabels: { display: false } } as unknown as ChartDataset] : []),
              { label: 'Số đơn', data: buckets.map(b => ordersBy.get(b)?.size || 0), type: 'line', yAxisID: 'y1', borderColor: '#059669', backgroundColor: '#05966922', borderWidth: 2, pointRadius: 2, tension: 0.3, datalabels: { display: false } } as unknown as ChartDataset,
            ]}
            options={{
              scales: {
                x: { stacked: true, grid: { display: false } },
                y: { stacked: true, beginAtZero: true, ticks: { callback: (v: string | number) => `${v} tr` } },
                y1: { position: 'right', beginAtZero: true, suggestedMax: maxOrders * 2.5, grid: { drawOnChartArea: false }, ticks: { precision: 0 } },
              },
            }}
          />
        </div>
      </section>

      <section className="grid three space atop">
        <div className="card">
          <div className="section-title"><h2>Tỷ trọng cũ / mới</h2><span>{mil(t.total)}</span></div>
          <div className="chart small">
            <ChartBox type="doughnut" labels={['Hàng cũ', 'Bán kèm mới']}
              datasets={[{ data: [toMil(t.usedRev), toMil(t.newRev)], backgroundColor: [USED_COLOR, NEW_COLOR], borderWidth: 0 } as unknown as ChartDataset]}
              options={{ cutout: '60%', scales: { x: { display: false }, y: { display: false } } } as never} />
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Theo cửa hàng</h2><span>bấm để lọc</span></div>
          <div className="chart small">
            <ChartBox type="doughnut" labels={storeShare.map(e => e[0])}
              datasets={[{ data: storeShare.map(e => toMil(e[1])), backgroundColor: storeShare.map((_, i) => PIE_COLORS[i % PIE_COLORS.length]), borderWidth: 0 } as unknown as ChartDataset]}
              options={{ cutout: '60%', scales: { x: { display: false }, y: { display: false } } } as never}
              onPointClick={({ index }) => { const s = storeShare[index]?.[0]; if (s) { setStore(store === s ? '' : s); setSale(''); } }} />
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Theo sale</h2><span>tổng DT đơn · bấm để lọc</span></div>
          <div className="chart small">
            <ChartBox labels={saleShare.map(e => e[0])}
              datasets={[
                { label: 'Hàng cũ', data: saleShare.map(e => toMil(saleUsed.get(e[0]))), backgroundColor: `${USED_COLOR}bb`, stack: 's', borderRadius: 3, datalabels: NO_LABEL } as unknown as ChartDataset,
                { label: 'Bán kèm', data: saleShare.map(e => toMil(e[1] - (saleUsed.get(e[0]) || 0))), backgroundColor: `${NEW_COLOR}99`, stack: 's', borderRadius: 3, datalabels: totalLabel(saleShare.map(e => toMil(e[1]))) } as unknown as ChartDataset,
              ]}
              options={{ indexAxis: 'y', layout: { padding: { right: 28 } }, scales: { x: { stacked: true, beginAtZero: true }, y: { stacked: true, grid: { display: false } } } }}
              onPointClick={({ index }) => { const s = saleShare[index]?.[0]; if (s) setSale(sale === s ? '' : s); }} />
          </div>
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Pivot theo SKU hàng cũ</h2>
          <span>DT bán kèm của đơn chia cho từng SKU cũ theo tỷ trọng doanh thu hàng cũ trong đơn</span>
        </div>
        <div className="scroll">
          <table>
            <thead><tr>
              <th>SKU hàng cũ</th><th className="right">Đơn</th><th className="right">SL</th><th className="right">DT hàng cũ</th>
              <th className="right">DT bán kèm (mới)</th><th className="right">Tổng DT</th><th className="right">Tỷ trọng</th>
              {prevPeriod && <><th className="right">Tổng DT kỳ trước</th><th className="right">±</th></>}
            </tr></thead>
            <tbody>
              {pivotRows.length === 0 && prevOnly.length === 0 && <tr><td colSpan={9} className="muted">Không có đơn hàng cũ trong kỳ.</td></tr>}
              {pivotRows.map(r => {
                const pr = pivotPrev.get(r.sku);
                const d = prevPeriod && pr ? deltaOf(r.total, pr.total) : null;
                return (
                  <tr key={r.sku}>
                    <td><b>{r.sku}</b><div className="muted small-text">{r.name}</div></td>
                    <td className="right tnum">{num(r.orders)}</td>
                    <td className="right tnum">{num(r.qty)}</td>
                    <td className="right tnum">{vnd(r.usedRev)}</td>
                    <td className="right tnum">{r.bundled ? vnd(r.bundled) : '—'}</td>
                    <td className="right tnum"><b>{vnd(r.total)}</b></td>
                    <td className="right tnum">{t.total ? `${((r.total / t.total) * 100).toFixed(1)}%` : '—'}</td>
                    {prevPeriod && <><td className="right tnum muted">{pr ? vnd(pr.total) : '—'}</td><td className="right">{d && <Tag tone={d.tone}>{d.text}</Tag>}</td></>}
                  </tr>
                );
              })}
              {prevOnly.map(r => (
                <tr key={`prev-${r.sku}`} className="muted">
                  <td>{r.sku}<div className="small-text">{r.name} · chỉ bán kỳ trước</div></td>
                  <td className="right tnum">0</td><td className="right tnum">0</td><td className="right tnum">—</td><td className="right tnum">—</td>
                  <td className="right tnum">0</td><td className="right tnum">—</td>
                  <td className="right tnum">{vnd(r.total)}</td><td className="right"><Tag tone="red">▼ -100%</Tag></td>
                </tr>
              ))}
              {pivotRows.length > 0 && (
                <tr className="total-row">
                  <td><b>Tổng</b></td>
                  <td className="right tnum"><b>{num(t.orders)}</b></td>
                  <td className="right tnum"><b>{num(t.usedQty)}</b></td>
                  <td className="right tnum"><b>{vnd(t.usedRev)}</b></td>
                  <td className="right tnum"><b>{vnd(t.newRev)}</b></td>
                  <td className="right tnum"><b>{vnd(t.total)}</b></td>
                  <td className="right tnum">100%</td>
                  {prevPeriod && p && <><td className="right tnum"><b>{vnd(p.total)}</b></td><td className="right">{(() => { const d = deltaOf(t.total, p.total); return d && <Tag tone={d.tone}>{d.text}</Tag>; })()}</td></>}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Chi tiết đơn hàng</h2>
          <span>{num(t.orders)} đơn · {num(detail.length)} dòng · hàng cũ đứng đầu mỗi đơn</span>
        </div>
        <div className="scroll">
          <table>
            <thead><tr>
              <th>Ngày</th><th>Mã đơn</th><th>Sale</th><th>SKU</th><th className="right">SL</th>
              <th className="right">Đơn giá</th><th className="right">Thành tiền</th><th>Cũ / mới</th>
            </tr></thead>
            <tbody>
              {detail.length === 0 && <tr><td colSpan={8} className="muted">Không có đơn hàng cũ trong kỳ.</td></tr>}
              {detail.slice(0, detailLimit).map((l, i) => {
                const firstOfOrder = i === 0 || detail[i - 1].oid !== l.oid;
                const band = (orderIndex.get(l.oid) || 0) % 2 === 1;
                return (
                  <Fragment key={`${l.oid}-${l.sku}-${i}`}>
                    <tr style={{ background: band ? 'var(--surface-2)' : undefined, borderTop: firstOfOrder ? '2px solid var(--line)' : undefined }}>
                      <td className="tnum">{firstOfOrder ? ddmm(l.d) : ''}</td>
                      <td className="tnum">{firstOfOrder ? <span title={l.st || ''}>{l.code || l.oid}</span> : ''}</td>
                      <td>{firstOfOrder ? <>{l.sp}<div className="muted small-text">{l.store}</div></> : ''}</td>
                      <td><b>{l.sku}</b><div className="muted small-text">{l.name}</div></td>
                      <td className="right tnum">{num(l.qty)}</td>
                      <td className="right tnum">{vnd(l.list / Math.max(1, l.qty))}</td>
                      <td className="right tnum">{vnd(l.rev)}{l.disc > 0 && <div className="muted small-text">CK {vnd(l.disc)}</div>}</td>
                      <td>{l.used ? <Tag tone="violet">Hàng cũ</Tag> : l.gift ? <Tag tone="gray">Quà tặng</Tag> : <Tag tone="blue">Hàng mới</Tag>}</td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {detail.length > detailLimit && (
          <button type="button" className="ins-more" onClick={() => setDetailLimit(n => n + 300)}>Xem thêm · còn {num(detail.length - detailLimit)} dòng</button>
        )}
      </section>
    </>
  );
}
